import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { ensureMotion, prefersReducedMotion } from '@/lib/universe/motion'
import { replayReveals } from '@/lib/universe/gravity'
import { beginIntro, endIntro, subscribeFrame } from '@/lib/universe/store'

const TAU = Math.PI * 2

// The disk seen at a slant, as on the opening screen
const TILT = 0.27

// Where the shadow ends, as a share of the shorter side of the window
const SHADOW = 0.075

// Sizes are shares of the shorter side of the window
const DISK_INNER = 0.1
const DISK_SPAN = 0.21

// The moment the screen is black inside the shadow and the page takes over
const HANDOVER = 4.25

interface Particle {
    radius: number
    angle: number
    omega: number
    size: number
    alpha: number
    delay: number
    reach: number
    swirl: number
    tone: number
}

interface IntroState {
    gather: number
    collapse: number
    grow: number
    lens: number
    spin: number
    zoom: number
    fade: number
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const easeInOut = (value: number): number => (value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2)

const makeParticles = (count: number): Array<Particle> => Array.from({ length: count }, () => {
    const spread = Math.pow(Math.random(), 0.85)
    const radius = DISK_INNER + DISK_SPAN * spread

    return {
        radius,
        angle: Math.random() * TAU,
        // Inner gas turns faster than outer gas
        omega: 0.9 * Math.pow(DISK_INNER / radius, 1.5),
        size: 0.6 + Math.random() * 1.7,
        alpha: 0.3 + Math.random() * 0.7,
        delay: Math.random() * 0.4,
        reach: 0.55 + Math.random() * 0.75,
        swirl: 2.4 + Math.random() * 2.8,
        tone: spread < 0.25 ? 0 : spread < 0.62 ? 1 : 2
    }
})

// A soft round point of light, drawn once and stamped for every particle
const makeSprite = (core: string, middle: string): HTMLCanvasElement => {
    const sprite = document.createElement('canvas')

    sprite.width = 32
    sprite.height = 32

    const context = sprite.getContext('2d')

    if (context) {
        const gradient = context.createRadialGradient(16, 16, 0, 16, 16, 16)

        gradient.addColorStop(0, core)
        gradient.addColorStop(0.32, middle)
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
        context.fillStyle = gradient
        context.fillRect(0, 0, 32, 32)
    }

    return sprite
}

// Plays once, when the site first loads: golden light swirls in and settles into a disk, the
// shadow opens at its centre, the disk spins faster and faster, and the camera falls into the
// shadow. Inside it, the real black hole takes over and pulls back out to the page.
export const Intro: FC = (): ReactNode => {
    const [playing, setPlaying] = useState(true)
    const layerRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const skipRef = useRef<() => void>(() => undefined)

    useEffect(() => {
        const layer = layerRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!layer || !canvas || !context) {
            setPlaying(false)

            return
        }

        const gsap = ensureMotion()
        const root = document.documentElement

        // Visitors who prefer less motion get a short fade instead
        if (prefersReducedMotion()) {
            const tween = gsap.to(layer, {
                opacity: 0,
                duration: 0.5,
                delay: 0.2,
                ease: 'none',
                onComplete: () => setPlaying(false)
            })

            return () => {
                tween.kill()
            }
        }

        beginIntro({ distance: 2.3, fov: 60, shiftX: 0, shiftY: 0, streak: 0 })
        root.style.overflow = 'hidden'

        const compact = window.innerWidth < 768
        const particles = makeParticles(compact ? 650 : 1300)
        const sprites = [
            makeSprite('rgba(255, 249, 236, 1)', 'rgba(255, 222, 168, 0.5)'),
            makeSprite('rgba(255, 224, 164, 1)', 'rgba(232, 164, 76, 0.42)'),
            makeSprite('rgba(242, 176, 96, 0.9)', 'rgba(170, 82, 26, 0.36)')
        ]
        const state: IntroState = { gather: 0, collapse: 0, grow: 0, lens: 0, spin: 1, zoom: 0, fade: 0 }
        const px = new Float32Array(particles.length)
        const py = new Float32Array(particles.length)
        const pa = new Float32Array(particles.length)
        const ps = new Float32Array(particles.length)
        let handedOver = false

        const handOver = (): void => {
            if (handedOver) return

            handedOver = true
            endIntro()
            replayReveals()
            root.style.overflow = ''
        }

        let teardown = (): void => undefined

        const timeline = gsap.timeline({
            onComplete: () => {
                teardown()
                setPlaying(false)
            }
        })

        timeline
            .to(state, { gather: 1, duration: 1.9, ease: 'power2.inOut' }, 0)
            .to(state, { grow: 1, duration: 2.1, ease: 'power2.inOut' }, 0.9)
            .to(state, { collapse: 1, duration: 1.2, ease: 'power3.out' }, 1.15)
            .to(state, { lens: 1, duration: 1, ease: 'power1.out' }, 1.8)
            .to(state, { spin: 7, duration: 2.1, ease: 'power2.in' }, 1.2)
            .to(state, { zoom: 1, duration: 1.1, ease: 'power3.in' }, 3.15)
            .to(state, { fade: 1, duration: 0.6, ease: 'power1.inOut', onStart: handOver }, HANDOVER)

        // A click, a key or a scroll hurries the intro to its end instead of cutting it
        skipRef.current = () => {
            if (handedOver || timeline.time() >= 3.15) {
                timeline.timeScale(2.5)

                return
            }

            timeline.seek(3.15)
            timeline.timeScale(2.2)
        }

        const onWheel = (): void => skipRef.current()
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Tab' || event.key === 'Shift') return

            skipRef.current()
        }

        window.addEventListener('wheel', onWheel, { passive: true })
        window.addEventListener('touchmove', onWheel, { passive: true })
        window.addEventListener('keydown', onKey)

        const stop = subscribeFrame((_frame, dt) => {
            const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
            const width = Math.round(window.innerWidth * dpr)
            const height = Math.round(window.innerHeight * dpr)

            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width
                canvas.height = height
            }

            const base = Math.min(width, height)
            const diagonal = Math.hypot(width, height) * 0.5
            const cx = width / 2
            const cy = height / 2
            const scale = (0.42 + 0.58 * state.grow) * Math.pow(18, state.zoom)
            const shadow = base * SHADOW * scale * state.collapse
            const innerEdge = base * DISK_INNER * scale
            const limit = diagonal * 1.25

            layer.style.opacity = (1 - state.fade).toFixed(3)

            // Fading the last frame instead of clearing it leaves short trails behind the light
            context.globalCompositeOperation = 'source-over'
            context.globalAlpha = 1
            context.fillStyle = `rgba(0, 0, 0, ${(0.24 - 0.1 * state.zoom).toFixed(3)})`
            context.fillRect(0, 0, width, height)

            for (let index = 0; index < particles.length; index++) {
                const particle = particles[index]

                particle.angle += particle.omega * state.spin * dt

                const gathered = easeInOut(clamp01((state.gather - particle.delay) / 0.6))
                const orbit = particle.radius * base * scale
                const radius = particle.reach * diagonal + (orbit - particle.reach * diagonal) * gathered
                const angle = particle.angle + (1 - gathered) * particle.swirl
                const flatten = 1 + (TILT - 1) * gathered * gathered
                const cos = Math.cos(angle)
                const sin = Math.sin(angle)

                px[index] = cos * radius
                py[index] = sin * radius * flatten
                // The side turning towards the viewer burns brighter
                pa[index] = particle.alpha * (0.3 + 0.7 * (0.5 + 0.5 * cos)) * (0.25 + 0.75 * gathered)
                ps[index] = Math.min(64 * dpr, particle.size * dpr * (1.6 + 2.2 * Math.min(1, scale)) * (1 + 3 * state.zoom * state.zoom))
            }

            context.globalCompositeOperation = 'lighter'

            // The back of the disk, and its light bent over and under the shadow
            for (let index = 0; index < particles.length; index++) {
                if (py[index] >= 0) continue

                const x = px[index]
                const y = py[index]
                const size = ps[index]

                if (Math.abs(x) > limit || Math.abs(y) > limit) continue

                const sprite = sprites[particles[index].tone]

                context.globalAlpha = pa[index]
                context.drawImage(sprite, cx + x - size / 2, cy + y - size / 2, size, size)

                if (state.lens > 0 && shadow > 1) {
                    const orbit = particles[index].radius * base * scale
                    const u = Math.max(-1, Math.min(1, x / Math.max(orbit, 1)))
                    const v = Math.sqrt(1 - u * u)
                    const rho = shadow * 1.1 + (orbit - innerEdge) * 0.42
                    const small = size * 0.8

                    context.globalAlpha = pa[index] * 0.5 * state.lens
                    context.drawImage(sprite, cx + rho * u - small / 2, cy - rho * v * 0.95 - small / 2, small, small)
                    context.globalAlpha = pa[index] * 0.16 * state.lens
                    context.drawImage(sprite, cx + rho * u * 0.92 - small / 2, cy + rho * v * 0.85 - small / 2, small, small)
                }
            }

            // Light falling to the centre, until the shadow opens and swallows it
            const glow = state.gather * (1 - state.collapse) * (1 - state.zoom)

            if (glow > 0.01) {
                const size = base * 0.32 * (0.6 + 0.4 * state.gather)

                context.globalAlpha = glow * 0.55
                context.drawImage(sprites[0], cx - size / 2, cy - size / 2, size, size)
            }

            if (shadow > 0.5) {
                const ring = Math.min(1, state.collapse * 1.25)

                context.globalCompositeOperation = 'source-over'
                context.globalAlpha = 1
                context.fillStyle = '#000'
                context.beginPath()
                context.arc(cx, cy, shadow, 0, TAU)
                context.fill()

                context.globalCompositeOperation = 'lighter'
                context.lineWidth = Math.max(2 * dpr, shadow * 0.09)
                context.strokeStyle = `rgba(232, 160, 80, ${(0.18 * ring).toFixed(3)})`
                context.beginPath()
                context.arc(cx, cy, shadow * 1.06, 0, TAU)
                context.stroke()

                context.lineWidth = Math.max(dpr, dpr * 1.3 * Math.min(4, Math.sqrt(scale)))
                context.strokeStyle = `rgba(255, 243, 224, ${(0.92 * ring).toFixed(3)})`
                context.beginPath()
                context.arc(cx, cy, shadow * 1.03, 0, TAU)
                context.stroke()
            }

            // The front of the disk crosses in front of the shadow
            for (let index = 0; index < particles.length; index++) {
                if (py[index] < 0) continue

                const x = px[index]
                const y = py[index]
                const size = ps[index]

                if (Math.abs(x) > limit || Math.abs(y) > limit) continue

                context.globalAlpha = pa[index]
                context.drawImage(sprites[particles[index].tone], cx + x - size / 2, cy + y - size / 2, size, size)
            }

            context.globalAlpha = 1
        })

        // The component stays mounted after the intro, so the loop and listeners are released here
        let tornDown = false

        teardown = () => {
            if (tornDown) return

            tornDown = true
            stop()
            window.removeEventListener('wheel', onWheel)
            window.removeEventListener('touchmove', onWheel)
            window.removeEventListener('keydown', onKey)
            handOver()
        }

        return () => {
            timeline.kill()
            teardown()
        }
    }, [])

    if (!playing) return null

    return (
        <div ref={layerRef} id='umbra-intro' className='fixed inset-0 z-[55] bg-void'>
            <canvas
                ref={canvasRef}
                aria-hidden='true'
                onClick={() => skipRef.current()}
                className='absolute inset-0 block h-full w-full'
            />

            <button
                type='button'
                onClick={() => skipRef.current()}
                className='label-caps link-line absolute bottom-6 right-6 text-muted-foreground/70 transition-colors duration-500 hover:text-foreground md:bottom-8 md:right-10'
            >
                Skip
            </button>
        </div>
    )
}
